import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subject, timer, from, of } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, exhaustMap, filter, finalize, map, retry, startWith, take, takeUntil, takeWhile, toArray } from 'rxjs/operators';
import { DataFetch, DataQuery } from '../../models/data-source';
import { DataListComponent } from '../../shared/ui/data-list/data-list.component';
import { DataTableColumn, DataTableComponent } from '../../shared/ui/data-table/data-table.component';
import { AuthService } from '../../services/auth.service';
import { ConfirmationDialogService } from '../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import { triggerBlobDownload } from '../../shared/download.util';
import { FileDetail, FileItem, FileProcessing, FileRevision, FilesService, FILE_UPLOAD_MAX_BYTES } from './files.service';
import { WorkspaceBreadcrumb } from '../../components/workspace/workspace-breadcrumbs.component';
import { WorkspaceBreadcrumbTrail } from '../../components/workspace/workspace-breadcrumb-trail.service';
import { FileLocation } from './file-tree.component';
import { FILE_MOVE_TYPE } from './file-drop-target.directive';

/** Formats the OCR service accepts; other files cannot start a run. */
export const OCR_CONTENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'];

@Component({ selector: 'app-files', host: { class: 'workspace-master-detail-page' }, templateUrl: './files.component.html', styleUrls: ['./files.component.css'] })
export class FilesComponent implements OnInit, OnDestroy {
  @ViewChild(DataListComponent) list?: DataListComponent<FileItem>;
  @ViewChild(DataTableComponent) table?: DataTableComponent<FileItem>;
  readonly columns: DataTableColumn[] = [
    { id: 'name', label: 'Name' },
    { id: 'byteSize', label: 'Size', align: 'end' },
    { id: 'uploadedByName', label: 'Uploaded by' },
    { id: 'uploadedAt', label: 'Uploaded at', align: 'end' },
  ];
  tableQuery: Pick<DataQuery, 'sort' | 'desc'> = { sort: 'name', desc: false };
  readonly trackFile = (_index: number, file: FileItem): string => file.id;
  readonly fetch: DataFetch<FileItem> = query => this.files.list(query, this.area, this.parentId);
  layout: 'table' | 'grid' = 'table';
  area = 'personal'; parentId: string | null = null;
  parent: FileItem | null = null; search = ''; error = ''; notice = ''; busy = false;
  treeOpen = false; treeRevision = 0; dragDepth = 0;
  folderName = ''; creatingFolder = false; detail: FileDetail | null = null; editedName = ''; renaming = false; processingError = '';
  draggedFile: FileItem | null = null;
  summary = ''; summarizing = false; summaryError = '';
  previewUrl: SafeResourceUrl | null = null; private blobUrl: string | null = null;
  private activeWrites = 0;
  private destroyed = new Subject<void>();
  private detailChanged = new Subject<void>();
  constructor(public files: FilesService, public auth: AuthService, private route: ActivatedRoute,
    private router: Router, private sanitizer: DomSanitizer,
    private confirmation: ConfirmationDialogService, private breadcrumbTrail: WorkspaceBreadcrumbTrail) {}
  get canManage(): boolean { return this.auth.hasPermission('File.Manage'); }
  get canUpload(): boolean { return this.canManage; }
  get ready(): FileRevision | undefined { return this.detail?.revisions.find(r => r.status === 'Ready'); }
  get canUploadRevision(): boolean { return !!this.detail && !this.detail.file.isFolder; }
  get canRunOcr(): boolean { return !!this.ready && OCR_CONTENT_TYPES.includes(this.ready.contentType); }
  /** The newest run for the current revision; older runs stay in the history below. */
  get currentOcr(): FileProcessing | undefined { return this.detail?.processing.find(r => r.revisionId === this.ready?.id); }
  get olderOcr(): FileProcessing[] { const current = this.currentOcr; return (this.detail?.processing ?? []).filter(r => r !== current); }
  get canPreview(): boolean { return !!this.ready && ['application/pdf', 'image/png', 'image/jpeg'].includes(this.ready.contentType); }
  typeLabel(file: FileItem): string { return file.name.includes('.') ? file.name.split('.').pop()!.toUpperCase() : 'File'; }
  startRename(): void { if (this.detail) { this.editedName = this.detail.file.name; this.renaming = true; } }
  cancelRename(): void { this.renaming = false; this.editedName = this.detail?.file.name ?? ''; }
  get ocrActive(): boolean { return this.currentOcr?.status === 'Pending' || this.currentOcr?.status === 'Running'; }
  get areaLabel(): string { return this.area === 'shared' ? 'Shared Files' : 'My Files'; }
  ngOnInit(): void {
    // The folder is the child path segment and the area a query parameter; both live in the URL.
    this.router.events.pipe(filter(e => e instanceof NavigationEnd), startWith(null), map(() => this.location()),
      distinctUntilChanged((a, b) => a.area === b.area && a.folder === b.folder), takeUntil(this.destroyed)).subscribe(location => {
      this.area = location.area;
      this.parentId = location.folder;
      this.showFolderTrail([]);
      this.endFileDrag(); this.dragDepth = 0; this.parent = null; this.creatingFolder = false; this.closeDetail(); this.search = ''; this.error = '';
      if (this.parentId) this.files.get(this.parentId).pipe(takeUntil(this.destroyed)).subscribe({
        next: d => { if (d.file.id === this.parentId) { this.parent = d.file; } },
        error: e => this.fail(e),
      });
      this.reload();
    });
  }
  ngOnDestroy(): void { this.breadcrumbTrail.clear(); this.detailChanged.next(); this.detailChanged.complete(); this.destroyed.next(); this.destroyed.complete(); this.clearPreview(); }
  private location(): { area: string; folder: string | null } {
    const snapshot = this.route.snapshot;
    return {
      area: snapshot.queryParamMap.get('area') === 'shared' ? 'shared' : 'personal',
      folder: snapshot.firstChild?.paramMap.get('folder') ?? null,
    };
  }
  private areaParams(area: string): Record<string, string> { return area === 'shared' ? { area } : {}; }
  navigate(area: string, folder: string | null = null): void {
    void this.router.navigate(folder ? ['/files', folder] : ['/files'], { queryParams: this.areaParams(area) });
  }
  /** Extends the Workspace breadcrumb with the folder ancestors; the page title names the current location. */
  showFolderTrail(path: FileItem[]): void {
    const ancestors = this.parentId && path[path.length - 1]?.id === this.parentId ? path.slice(0, -1) : [];
    // The Files entry comes from the route; the page adds My Files / ancestors and its title names the current location.
    const trail: WorkspaceBreadcrumb[] = [];
    if (this.parentId) trail.push({ label: this.areaLabel, path: '/files', queryParams: this.areaParams(this.area) });
    trail.push(...ancestors.map(folder => ({ label: folder.name, path: `/files/${folder.id}`, queryParams: this.areaParams(this.area) })));
    this.breadcrumbTrail.set(trail);
  }
  reload(refreshTree = false): void { this.list?.reload(); this.table?.reload(); if (refreshTree) this.treeRevision++; }
  selectLocation(location: FileLocation): void {
    this.treeOpen = false; this.navigate(location.area, location.folder);
  }
  open(file: FileItem): void {
    if (file.isFolder) { this.navigate(file.isShared ? 'shared' : 'personal', file.id); return; }
    this.download(file);
  }
  inspect(file: FileItem): void {
    this.closeDetail();
    this.files.get(file.id).pipe(takeUntil(this.detailChanged), takeUntil(this.destroyed)).subscribe({
      next: d => { this.detail = d; this.editedName = d.file.name; this.summary = d.summary?.summary ?? ''; this.pollProcessing(d); const latest = d.processing.find(r => r.revisionId === this.ready?.id && (r.status === 'Succeeded' || r.status === 'Partial')); if (latest) this.readProcessing(latest); }, error: e => this.fail(e),
    });
  }
  private pollProcessing(detail: FileDetail, firstDelay = 5000): void {
    const run = detail.processing.find(r => r.status === 'Pending' || r.status === 'Running');
    if (!run || !this.canManage) return;
    // Transient refresh failures are retried; if they persist, polling stops and Retry resumes it.
    timer(firstDelay, 5000).pipe(take(180), exhaustMap(() => this.files.refresh(detail.file.id, run.id).pipe(retry({ count: 2, delay: 5000 }))),
      takeWhile(result => result.status === 'Pending' || result.status === 'Running', true),
      takeUntil(this.detailChanged), takeUntil(this.destroyed)).subscribe({
        next: result => {
          if (this.detail?.file.id === detail.file.id) {
            this.detail.processing = this.detail.processing.map(r => r.id === result.id ? result : r);
          }
        }, error: e => { this.processingError = this.errorMessage(e); },
      });
  }
  /** Only offered after polling stopped on an error, so it never starts a second timer. */
  retryProcessing(): void {
    const detail = this.detail; if (!this.processingError || !detail) return;
    this.processingError = ''; this.pollProcessing(detail, 0);
  }
  closeDetail(): void {
    this.detailChanged.next(); this.clearPreview(); this.detail = null; this.processingError = ''; this.renaming = false;
    this.summary = ''; this.summarizing = false; this.summaryError = '';
  }
  private clearPreview(): void {
    this.previewUrl = null;
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
    this.blobUrl = null;
  }
  private errorMessage(e: { error?: { message?: string; error?: string } }): string {
    return e.error?.message ?? e.error?.error ?? 'The request failed. Please try again.';
  }
  private fail(e: { error?: { message?: string; error?: string } }): void { this.error = this.errorMessage(e); }
  private work<T>(request: Observable<T>, next: (value: T) => void): void {
    this.activeWrites++; this.busy = true; this.error = ''; this.notice = '';
    request.pipe(takeUntil(this.destroyed), finalize(() => { this.busy = --this.activeWrites > 0; })).subscribe({ next, error: e => this.fail(e) });
  }
  createFolder(): void {
    this.work(this.files.folder(this.folderName.trim(), this.parentId, this.area === 'shared'), () => { this.folderName = ''; this.creatingFolder = false; this.reload(true); });
  }
  upload(event: Event, revision = false): void {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []); input.value = '';
    this.uploadFiles(selected, revision);
  }
  dragOver(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = this.canUpload && !this.busy ? 'copy' : 'none';
  }
  dragEnter(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    if (this.canUpload && !this.busy) this.dragDepth++;
  }
  dragLeave(event: DragEvent): void { event.preventDefault(); this.dragDepth = Math.max(0, this.dragDepth - 1); }
  drop(event: DragEvent): void {
    event.preventDefault(); this.dragDepth = 0;
    if (event.dataTransfer?.types.includes(FILE_MOVE_TYPE)) { this.endFileDrag(); return; }
    if (!this.canUpload || this.busy) return;
    if (Array.from(event.dataTransfer?.items ?? []).some(item => item.webkitGetAsEntry?.()?.isDirectory)) {
      this.error = 'Drop files, not folders. Create a folder first, then upload its files.'; return;
    }
    this.uploadFiles(Array.from(event.dataTransfer?.files ?? []));
  }
  uploadFiles(selected: File[], revision = false): void {
    if (!selected.length || !this.canUpload || this.busy) return;
    if (revision && (selected.length !== 1 || !this.canUploadRevision)) return;
    const failures: string[] = [];
    const valid = selected.filter(file => {
      if (file.size > 0 && file.size <= FILE_UPLOAD_MAX_BYTES) return true;
      failures.push(file.name + ': choose a nonempty file up to 1 GB.'); return false;
    });
    this.error = ''; this.notice = '';
    if (!valid.length) { this.error = failures.join(' '); return; }
    // Capture the destination before async work; navigation cannot redirect queued uploads.
    const parentId = this.parentId, area = this.area;
    const targetId = revision ? this.detail?.file.id : undefined;
    const shared = revision ? !!this.detail?.file.isShared : area === 'shared';
    if (revision && !targetId) return;
    const request = from(valid).pipe(concatMap(file =>
      this.files.upload(file, parentId, shared, targetId).pipe(
        catchError(e => { failures.push(file.name + ': ' + this.errorMessage(e)); return of(null); }),
      )), toArray());
    this.work(request, results => {
      if (this.parentId !== parentId || this.area !== area) return;
      const uploaded = results.filter((result): result is NonNullable<typeof result> => result !== null);
      this.error = failures.join(' ');
      this.reload();
      if (revision && uploaded.length && this.detail && this.detail.file.id === targetId) this.inspect(this.detail.file);
    });
  }
  saveName(): void {
    const detail = this.detail; if (!detail || this.busy) return;
    const file = detail.file;
    this.work(this.files.update(file, this.editedName.trim(), file.parentId), () => {
      this.reload(file.isFolder);
      if (this.detail === detail) this.inspect(file);
    });
  }
  startFileDrag(event: DragEvent, file: FileItem): void {
    if (!this.canManage || this.busy || !event.dataTransfer) {
      event.preventDefault(); return;
    }
    this.draggedFile = file;
    event.dataTransfer.setData(FILE_MOVE_TYPE, file.id);
    event.dataTransfer.effectAllowed = 'move';
  }
  endFileDrag(): void { this.draggedFile = null; }
  readonly canDropFile = (location: FileLocation): boolean => {
    const file = this.draggedFile;
    return !!file && this.canManage && !this.busy
      && location.area === (file.isShared ? 'shared' : 'personal')
      && location.folder !== file.id && location.folder !== file.parentId;
  };
  dropFile(location: FileLocation): void {
    const file = this.draggedFile;
    if (!file || !this.canDropFile(location)) return;
    this.endFileDrag();
    this.work(this.files.update(file, file.name, location.folder), () => {
      if (this.detail?.file.id === file.id) this.closeDetail();
      this.reload(file.isFolder);
    });
  }
  remove(file: FileItem): void {
    if (!this.canManage || this.busy) return;
    const kind = file.isFolder ? 'folder' : 'file';
    this.confirmation.confirm({
      title: `Delete ${kind}?`,
      message: file.isFolder
        ? `Delete "${file.name}" and everything in it? Its files remain recoverable.`
        : `Remove "${file.name}" from Files? Its stored revisions are retained for recovery.`,
      confirmLabel: `Delete ${kind}`,
      danger: true,
    }).pipe(take(1), takeUntil(this.destroyed)).subscribe(confirmed => {
      if (!confirmed || !this.canManage || this.busy) return;
      this.work(this.files.delete(file), () => {
        if (this.detail?.file.id === file.id) this.closeDetail();
        this.reload(file.isFolder);
        this.notice = file.isFolder ? 'Folder removed.' : 'File removed.';
      });
    });
  }
  preview(revision: FileRevision): void {
    const id = this.detail?.file.id; if (!id) return;
    this.files.content(id, revision.id).pipe(takeUntil(this.detailChanged), takeUntil(this.destroyed)).subscribe({
      next: blob => {
        this.clearPreview();
        if (!['application/pdf', 'image/png', 'image/jpeg'].includes(blob.type)) { this.error = 'Preview is unavailable for this format. Use Download.'; return; }
        this.blobUrl = URL.createObjectURL(blob);
        // Only locally created blob URLs for server-validated image/PDF content enter this iframe.
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
      }, error: e => this.fail(e),
    });
  }
  download(file: FileItem, revision?: FileRevision): void {
    if (this.busy) return;
    this.work(this.files.content(file.id, revision?.id), blob => triggerBlobDownload(blob, file.name));
  }
  /** The agent decides whether to read the file or its OCR text; a slow run never blocks other file actions. */
  summarize(): void {
    const file = this.detail?.file;
    if (!file || !this.ready || this.summarizing) return;
    this.summarizing = true; this.summaryError = '';
    this.files.summary(file.id).pipe(takeUntil(this.detailChanged), takeUntil(this.destroyed),
      finalize(() => { this.summarizing = false; })).subscribe({
      next: result => { this.summary = result.summary; },
      error: e => { this.summaryError = this.errorMessage(e); },
    });
  }
  runOcr(): void {
    const file = this.detail?.file, revision = this.ready;
    if (!file || !revision || !this.canRunOcr || this.ocrActive) return;
    this.work(this.files.ocr(file.id, revision.id), () => this.inspect(file));
  }
  refreshOcr(): void {
    const file = this.detail?.file, run = this.currentOcr;
    if (!file || !run || !this.ocrActive) return;
    this.work(this.files.refresh(file.id, run.id), result => {
      if (this.detail?.file.id === file.id) this.detail.processing = this.detail.processing.map(r => r.id === result.id ? result : r);
    });
  }
  readProcessing(run: FileProcessing): void {
    const id = this.detail?.file.id; if (!id) return;
    this.files.processing(id, run.id).pipe(takeUntil(this.detailChanged), takeUntil(this.destroyed)).subscribe({
      next: result => { if (this.detail?.file.id === id) this.detail.processing = this.detail.processing.map(r => r.id === result.id ? result : r); },
      error: e => this.fail(e),
    });
  }
}
